import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Vitals, StatDose } from '../../types';
import { Activity, ArrowRight, History, Syringe, Trash2 } from 'lucide-react';
export const NursingDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters
  } = useData();
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  // Vitals form state
  const [vitals, setVitals] = useState<Partial<Vitals>>({
    temp: '',
    pulse: '',
    resp: '',
    bp: '',
    sp02: '',
    weight: '',
    height: '',
    bmi: '',
    painScore: '',
    avpu: 'Alert'
  });
  // Stat Dose form state
  const [statDoseForm, setStatDoseForm] = useState({
    drugName: '',
    dose: '',
    route: 'PO',
    notes: ''
  });
  // Show patients in NURSING stage AND any active patient (so nurses can administer stat doses anytime)
  const pendingEncounters = encounters.filter(e => e.stage === 'NURSING');
  const activeEncounters = encounters.filter(e => e.stage !== 'COMPLETED' && e.stage !== 'NURSING');
  const handleAddStatDose = () => {
    if (!selectedEncounter || !currentUser || !statDoseForm.drugName) return;
    const newDose: StatDose = {
      id: `stat-${Date.now()}`,
      drugName: statDoseForm.drugName,
      dose: statDoseForm.dose,
      route: statDoseForm.route,
      notes: statDoseForm.notes,
      givenAt: new Date().toISOString(),
      givenBy: currentUser.id,
      nurseName: currentUser.fullName
    };
    const existing = selectedEncounter.statDoses || [];
    updateEncounter(selectedEncounter.id, {
      statDoses: [...existing, newDose]
    });
    // Refresh selected encounter to show new dose
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: [...existing, newDose]
    });
    setStatDoseForm({
      drugName: '',
      dose: '',
      route: 'PO',
      notes: ''
    });
  };
  const handleRemoveStatDose = (doseId: string) => {
    if (!selectedEncounter) return;
    const filtered = (selectedEncounter.statDoses || []).filter(d => d.id !== doseId);
    updateEncounter(selectedEncounter.id, {
      statDoses: filtered
    });
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: filtered
    });
  };
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounter(encounter);
    setVitals({
      temp: '',
      pulse: '',
      resp: '',
      bp: '',
      sp02: '',
      weight: '',
      height: '',
      bmi: '',
      painScore: '',
      avpu: 'Alert'
    });
  };
  const calculateBMI = (weightStr: string, heightStr: string) => {
    const weight = parseFloat(weightStr);
    const heightCm = parseFloat(heightStr);
    if (!isNaN(weight) && !isNaN(heightCm) && heightCm > 0) {
      const heightM = heightCm / 100;
      const bmi = (weight / (heightM * heightM)).toFixed(1);
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi
      }));
    } else {
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi: ''
      }));
    }
  };
  const handleSaveVitals = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEncounter || !currentUser) return;
    const completeVitals: Vitals = {
      ...(vitals as Vitals),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      vitals: completeVitals,
      stage: 'CLINICIAN'
    });
    alert('Vitals saved. Patient forwarded to Clinician.');
    setSelectedEncounter(null);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-1014">Patient not found</div>;
    const pastEncounters = getPatientEncounters(patient.id).filter(e => e.id !== selectedEncounter.id && e.vitals);
    return <div className="space-y-6" data-id="element-1015">
        <div className="flex justify-between items-center" data-id="element-1016">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1017">
            Nursing Assessment
          </h1>
          <Button variant="outline" onClick={() => setSelectedEncounter(null)} data-id="element-1018">
            Back to Queue
          </Button>
        </div>

        <Card className="bg-blue-50 border-blue-200" data-id="element-1019">
          <div className="flex justify-between items-start" data-id="element-1020">
            <div data-id="element-1021">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1022">
                {patient.firstName} {patient.lastName}
              </h2>
              <p className="text-sm text-gray-600" data-id="element-1023">
                BMC: {patient.bmcFileNumber} • {patient.age} yrs • {patient.sex}
              </p>
            </div>
            <div className="text-right" data-id="element-1024">
              <p className="text-sm font-medium text-red-600" data-id="element-1025">
                Allergies: {patient.allergies}
              </p>
              <p className="text-sm text-gray-600" data-id="element-1026">
                HIV Status: {patient.hivStatus}
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1027">
          <div className="lg:col-span-2" data-id="element-1028">
            <Card title="Enter Vitals" data-id="element-1029">
              <form onSubmit={handleSaveVitals} className="space-y-6" data-id="element-1030">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1031">
                  <Input label="Blood Pressure (mmHg)" placeholder="120/80" required value={vitals.bp} onChange={e => setVitals({
                  ...vitals,
                  bp: e.target.value
                })} data-id="element-1032" />
                  <Input label="Temperature (°C)" type="number" step="0.1" required value={vitals.temp} onChange={e => setVitals({
                  ...vitals,
                  temp: e.target.value
                })} data-id="element-1033" />
                  <Input label="Pulse (bpm)" type="number" required value={vitals.pulse} onChange={e => setVitals({
                  ...vitals,
                  pulse: e.target.value
                })} data-id="element-1034" />
                  <Input label="Resp. Rate (cpm)" type="number" required value={vitals.resp} onChange={e => setVitals({
                  ...vitals,
                  resp: e.target.value
                })} data-id="element-1035" />
                  <Input label="SpO2 (%)" type="number" required value={vitals.sp02} onChange={e => setVitals({
                  ...vitals,
                  sp02: e.target.value
                })} data-id="element-1036" />
                  <Input label="Pain Score (0-10)" type="number" min="0" max="10" required value={vitals.painScore} onChange={e => setVitals({
                  ...vitals,
                  painScore: e.target.value
                })} data-id="element-1037" />

                  <Input label="Weight (kg)" type="number" step="0.1" required value={vitals.weight} onChange={e => calculateBMI(e.target.value, vitals.height || '')} data-id="element-1038" />
                  <Input label="Height (cm)" type="number" step="0.1" required value={vitals.height} onChange={e => calculateBMI(vitals.weight || '', e.target.value)} data-id="element-1039" />
                  <Input label="BMI" readOnly value={vitals.bmi} className="bg-gray-50" data-id="element-1040" />
                </div>

                <div className="border-t border-gray-200 pt-4" data-id="element-1041">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1042">
                    AVPU Scale
                  </label>
                  <div className="flex space-x-4" data-id="element-1043">
                    {['Alert', 'Verbal', 'Pain', 'Unresponsive'].map(level => <label key={level} className="flex items-center" data-id="element-1044">
                          <input type="radio" name="avpu" value={level} checked={vitals.avpu === level} onChange={e => setVitals({
                      ...vitals,
                      avpu: e.target.value
                    })} className="mr-2" data-id="element-1045" />
                          {level}
                        </label>)}
                  </div>
                </div>

                <div className="flex justify-end pt-4" data-id="element-1046">
                  <Button type="submit" size="lg" data-id="element-1047">
                    Save & Forward to Clinician
                  </Button>
                </div>
              </form>
            </Card>

            {/* Stat / Start Dose Section */}
            <Card title="Stat / Start Dose Administered" className="mt-6" action={<Syringe className="w-5 h-5 text-pink-500" data-id="element-1049" />} data-id="element-1048">
              <p className="text-xs text-gray-500 mb-4" data-id="element-1050">
                Record any medication given to the patient as a start/stat dose.
                These will appear on the patient file and be visible to the
                clinician.
              </p>

              {/* Existing Stat Doses */}
              {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="mb-4 space-y-2" data-id="element-1051">
                    {selectedEncounter.statDoses.map(d => <div key={d.id} className="flex justify-between items-start bg-pink-50 border border-pink-200 rounded p-3 text-sm" data-id="element-1052">
                        <div data-id="element-1053">
                          <p className="font-bold text-gray-900" data-id="element-1054">
                            {d.drugName}{' '}
                            <span className="text-gray-600 font-normal" data-id="element-1055">
                              {d.dose} • {d.route}
                            </span>
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5" data-id="element-1056">
                            Given by {d.nurseName} •{' '}
                            {new Date(d.givenAt).toLocaleString()}
                          </p>
                          {d.notes && <p className="text-xs text-gray-700 mt-1 italic" data-id="element-1057">
                              {d.notes}
                            </p>}
                        </div>
                        <button onClick={() => handleRemoveStatDose(d.id)} className="text-gray-400 hover:text-red-600 p-1" title="Remove" data-id="element-1058">
                          <Trash2 className="w-4 h-4" data-id="element-1059" />
                        </button>
                      </div>)}
                  </div>}

              {/* Add New Stat Dose */}
              <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-4" data-id="element-1060">
                <Input label="Drug Name" className="col-span-2" placeholder="e.g. Paracetamol" value={statDoseForm.drugName} onChange={e => setStatDoseForm({
                ...statDoseForm,
                drugName: e.target.value
              })} data-id="element-1061" />
                <Input label="Dose" placeholder="e.g. 500mg" value={statDoseForm.dose} onChange={e => setStatDoseForm({
                ...statDoseForm,
                dose: e.target.value
              })} data-id="element-1062" />
                <div data-id="element-1063">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1064">
                    Route
                  </label>
                  <select value={statDoseForm.route} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  route: e.target.value
                })} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-1065">
                    <option value="PO" data-id="element-1066">Oral (PO)</option>
                    <option value="IV" data-id="element-1067">Intravenous (IV)</option>
                    <option value="IM" data-id="element-1068">Intramuscular (IM)</option>
                    <option value="SC" data-id="element-1069">Subcutaneous (SC)</option>
                    <option value="PR" data-id="element-1070">Per Rectum (PR)</option>
                    <option value="Topical" data-id="element-1071">Topical</option>
                  </select>
                </div>
                <Input label="Notes (optional)" className="col-span-2" placeholder="Reason or observation..." value={statDoseForm.notes} onChange={e => setStatDoseForm({
                ...statDoseForm,
                notes: e.target.value
              })} data-id="element-1072" />
              </div>
              <div className="mt-4 flex justify-end" data-id="element-1073">
                <Button type="button" onClick={handleAddStatDose} disabled={!statDoseForm.drugName} className="bg-pink-600 hover:bg-pink-700 text-white disabled:bg-gray-300" data-id="element-1074">
                  <Syringe className="w-4 h-4 mr-2" data-id="element-1075" /> Mark Stat Dose Given
                </Button>
              </div>
            </Card>
          </div>

          <div className="lg:col-span-1" data-id="element-1076">
            <Card title="Vitals History" className="h-full" data-id="element-1077">
              {pastEncounters.length > 0 ? <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2" data-id="element-1078">
                  {pastEncounters.map(enc => <div key={enc.id} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm" data-id="element-1079">
                      <p className="font-medium text-gray-900 mb-2 border-b pb-1" data-id="element-1080">
                        {new Date(enc.startedAt).toLocaleDateString()}
                      </p>
                      <div className="grid grid-cols-2 gap-2" data-id="element-1081">
                        <div data-id="element-1082">
                          <span className="text-gray-500" data-id="element-1083">BP:</span>{' '}
                          {enc.vitals?.bp}
                        </div>
                        <div data-id="element-1084">
                          <span className="text-gray-500" data-id="element-1085">Temp:</span>{' '}
                          {enc.vitals?.temp}°C
                        </div>
                        <div data-id="element-1086">
                          <span className="text-gray-500" data-id="element-1087">Pulse:</span>{' '}
                          {enc.vitals?.pulse}
                        </div>
                        <div data-id="element-1088">
                          <span className="text-gray-500" data-id="element-1089">SpO2:</span>{' '}
                          {enc.vitals?.sp02}%
                        </div>
                        <div data-id="element-1090">
                          <span className="text-gray-500" data-id="element-1091">Weight:</span>{' '}
                          {enc.vitals?.weight}kg
                        </div>
                        <div data-id="element-1092">
                          <span className="text-gray-500" data-id="element-1093">BMI:</span>{' '}
                          {enc.vitals?.bmi}
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-8 text-gray-500 flex flex-col items-center" data-id="element-1094">
                  <History className="w-8 h-8 mb-2 text-gray-400" data-id="element-1095" />
                  <p data-id="element-1096">No previous vitals recorded</p>
                </div>}
            </Card>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1097">
      <div className="flex justify-between items-center" data-id="element-1098">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1099">Nursing Queue</h1>
        <Badge variant="info" className="text-sm px-3 py-1" data-id="element-1100">
          {pendingEncounters.length} Patients Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-1101">
        <ul className="divide-y divide-gray-200" data-id="element-1102">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const waitTime = Math.floor((new Date().getTime() - new Date(encounter.startedAt).getTime()) / 60000);
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1103">
                  <div className="flex items-center" data-id="element-1104">
                    <div className="bg-blue-100 p-2 rounded-full mr-4" data-id="element-1105">
                      <Activity className="h-6 w-6 text-blue-600" data-id="element-1106" />
                    </div>
                    <div data-id="element-1107">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1108">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1109">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1110">
                        Waiting for: {waitTime} mins
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-1111">
                    Assess Patient <ArrowRight className="w-4 h-4 ml-2" data-id="element-1112" />
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1113">
              <Activity className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1114" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1115">
                Queue is empty
              </p>
              <p data-id="element-1116">No patients are currently waiting for nursing assessment.</p>
            </li>}
        </ul>
      </Card>
    </div>;
};